import { useTranslation } from 'react-i18next';

/**
 * Single phase row inside the Gantt chart. Renders the phase label, the bar,
 * the grid lines, and the drag handle. All drag logic is provided by
 * {@link useGanttDrag} via props.
 */
export function PhaseRow({
  phase,
  totalWeeks,
  months,
  isModified,
  isDraggingRef,
  trackRefs,
  onDragStart,
  onKeyboardDurationChange,
}) {
  const { t } = useTranslation();
  const startPct = (phase.startWeek / totalWeeks) * 100;
  const widthPct = Math.max((phase.durationWeeks / totalWeeks) * 100, 2);
  const showLabel = widthPct > 10;

  return (
    <div className="gantt-phase flex items-center" data-testid={`gantt-phase-${phase.id}`}>
      {/* Phase label */}
      <div className="w-[140px] sm:w-40 shrink-0 pr-4">
        <div className="text-xs font-semibold text-[#171A1D] leading-tight">
          {t('calcData.phases.' + phase.id)}
        </div>
        <div
          className="text-xs text-[#8A929B]"
          style={{ fontFamily: 'IBM Plex Mono, monospace' }}
        >
          {phase.effort} {t('estimates.days')} /{' '}
          <span className={isModified ? 'text-[#2867B2] font-semibold' : ''}>
            {phase.durationWeeks} {t('estimates.weeks')}
          </span>
        </div>
      </div>

      {/* Timeline track — overflow:visible so drag handle isn't clipped */}
      <div
        ref={el => { trackRefs.current[phase.id] = el; }}
        className="flex-1 relative h-9 bg-[#F5F6F7] rounded-lg"
        style={{ overflow: 'visible' }}
      >
        {/* Grid lines clipped inside track */}
        <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none">
          {Array.from({ length: months - 1 }, (_, i) => (
            <div
              key={`grid-${phase.id}-${i + 1}`}
              className="absolute top-0 bottom-0 w-px bg-[#DDE1E5]"
              style={{ left: `${((i + 1) * 4 / totalWeeks) * 100}%` }}
            />
          ))}
        </div>

        {/* Phase bar */}
        <div
          className="absolute top-1.5 bottom-1.5 rounded flex items-center select-none"
          style={{
            left: `${startPct}%`,
            width: `${widthPct}%`,
            backgroundColor: phase.color,
            transition: isDraggingRef.current ? 'none' : 'left 0.15s, width 0.15s',
          }}
        >
          {showLabel && (
            <span
              className="pl-2 font-data font-medium truncate pointer-events-none"
              style={{ fontSize: '12px', color: phase.color === 'var(--carbon-400)' ? 'var(--carbon-900)' : 'var(--surface-card)' }}
            >
              {phase.durationWeeks}
            </span>
          )}
        </div>

        {/* Drag handle — circle at right edge of bar */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white z-10 cursor-ew-resize shadow hover:scale-125 transition-transform focus-visible:scale-125 focus-visible:ring-2 focus-visible:ring-offset-1"
          style={{
            left: `calc(${startPct + widthPct}% - 8px)`,
            backgroundColor: phase.color,
          }}
          onMouseDown={e => onDragStart(e, phase.id)}
          onKeyDown={e => {
            const step = { ArrowLeft: -1, ArrowDown: -1, ArrowRight: 1, ArrowUp: 1 }[e.key];
            if (step || e.key === 'Home') {
              e.preventDefault();
              onKeyboardDurationChange?.(phase.id, e.key === 'Home' ? 1 : Math.max(1, phase.durationWeeks + step));
            }
          }}
          onTouchStart={e => {
            e.preventDefault();
            onDragStart({ preventDefault: () => {}, clientX: e.touches[0].clientX }, phase.id);
          }}
          data-testid={`phase-drag-handle-${phase.id}`}
          role="slider"
          tabIndex={0}
          aria-label={`${t('visual.edit')}: ${t('calcData.phases.' + phase.id)}, ${phase.durationWeeks} ${t('estimates.weeks')}`}
          aria-valuenow={phase.durationWeeks}
          aria-valuemin={1}
          aria-valuemax={Math.max(52, phase.durationWeeks)}
          title={t('gantt.dragHint')}
        />
      </div>
    </div>
  );
}
